import React, { useState, useMemo } from 'react';
import { 
  RefreshCw, 
  ChevronLeft, 
  ChevronRight, 
  CheckCircle2, 
  XCircle, 
  Clock, 
  Square, 
  ShieldCheck, 
  ShieldX, 
  SquareIcon,
  Users,
  UserCheck,
  UserX,
  UserMinus,
  MoreHorizontal,
  Eye,
  Trash2,
  AlertTriangle,
  Check,
  X,
  Loader2,
  Calendar,
  Mail,
  Phone,
  Building,
  Globe,
  Star,
  Clock as ClockIcon,
  Crown,
  UserCog
} from 'lucide-react';
import UserDetailsPopup from './UserDetailsPopup';

// Enhanced status configuration with accessibility
const STATUS_CONFIG = {
  active: { 
    color: '#10b981', 
    bg: 'rgba(16, 185, 129, 0.1)', 
    border: 'rgba(16, 185, 129, 0.2)',
    icon: CheckCircle2,
    text: 'Active',
    description: 'User has full access'
  },
  blocked: { 
    color: '#ef4444', 
    bg: 'rgba(239, 68, 68, 0.1)', 
    border: 'rgba(239, 68, 68, 0.2)',
    icon: XCircle,
    text: 'Blocked',
    description: 'User access is restricted'
  },
  pending: { 
    color: '#f59e0b', 
    bg: 'rgba(245, 158, 11, 0.1)', 
    border: 'rgba(245, 158, 11, 0.2)',
    icon: Clock,
    text: 'Pending',
    description: 'Awaiting approval'
  },
  inactive: { 
    color: '#6b7280', 
    bg: 'rgba(107, 114, 128, 0.1)', 
    border: 'rgba(107, 114, 128, 0.2)',
    icon: Square,
    text: 'Inactive',
    description: 'User account is inactive'
  }
};

// Enhanced date formatting utility
const formatDate = (dateString) => {
  if (!dateString || dateString === 'Invalid Date' || dateString === null) {
    return 'Never';
  }
  
  try {
    const date = new Date(dateString);
    if (isNaN(date.getTime())) {
      return 'Never';
    }
    
    const now = new Date();
    const diffTime = Math.abs(now - date);
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    
    if (diffDays === 0) {
      return 'Today';
    } else if (diffDays === 1) {
      return 'Yesterday';
    } else if (diffDays < 7) {
      return `${diffDays} days ago`;
    } else if (diffDays < 30) {
      const weeks = Math.floor(diffDays / 7);
      return `${weeks} week${weeks > 1 ? 's' : ''} ago`;
    } else if (diffDays < 365) {
      const months = Math.floor(diffDays / 30);
      return `${months} month${months > 1 ? 's' : ''} ago`;
    } else {
      return date.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric'
      });
    }
  } catch (error) {
    return 'Never';
  }
};

// Enhanced status badge component
const StatusBadge = ({ status, theme, currentThemeName }) => {
  const config = STATUS_CONFIG[status] || STATUS_CONFIG.pending;
  const Icon = config.icon;

  return (
    <span 
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 6,
        padding: '8px 12px',
        borderRadius: 20,
        color: config.color,
        backgroundColor: config.bg,
        border: `1px solid ${config.border}`,
        fontSize: 12,
        fontWeight: 600,
        transition: 'all 0.2s ease',
        cursor: 'default'
      }}
      title={config.description}
      role="status"
      aria-label={`User status: ${config.text} - ${config.description}`}
    >
      <Icon size={14} />
      <span>{config.text}</span>
    </span>
  );
};

// Enhanced action button component
const ActionButton = ({ 
  icon: Icon, 
  label, 
  onClick, 
  variant = 'secondary', 
  size = 'sm',
  loading = false,
  disabled = false,
  theme,
  currentThemeName 
}) => {
  const variants = {
    primary: {
      bg: currentThemeName === 'dark' ? '#3b82f6' : '#3b82f6',
      color: 'white',
      hoverBg: currentThemeName === 'dark' ? '#2563eb' : '#2563eb'
    },
    secondary: {
      bg: currentThemeName === 'dark' ? 'rgba(59, 130, 246, 0.1)' : 'rgba(59, 130, 246, 0.1)',
      color: currentThemeName === 'dark' ? '#3b82f6' : '#3b82f6',
      hoverBg: currentThemeName === 'dark' ? 'rgba(59, 130, 246, 0.2)' : 'rgba(59, 130, 246, 0.2)'
    },
    danger: {
      bg: currentThemeName === 'dark' ? 'rgba(239, 68, 68, 0.1)' : 'rgba(239, 68, 68, 0.1)',
      color: currentThemeName === 'dark' ? '#ef4444' : '#ef4444',
      hoverBg: currentThemeName === 'dark' ? 'rgba(239, 68, 68, 0.2)' : 'rgba(239, 68, 68, 0.2)'
    }
  };

  const sizes = {
    sm: { padding: '6px 12px', fontSize: 12 },
    md: { padding: '8px 16px', fontSize: 14 },
    lg: { padding: '12px 20px', fontSize: 16 }
  };

  const style = variants[variant];
  const sizeStyle = sizes[size];

  return (
    <button
      onClick={onClick}
      disabled={disabled || loading}
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 6,
        padding: sizeStyle.padding,
        fontSize: sizeStyle.fontSize,
        fontWeight: 600,
        borderRadius: 8,
        border: 'none',
        backgroundColor: style.bg,
        color: style.color,
        cursor: disabled || loading ? 'not-allowed' : 'pointer',
        transition: 'all 0.2s ease',
        opacity: disabled || loading ? 0.6 : 1,
        outline: 'none'
      }}
      onMouseEnter={(e) => {
        if (!disabled && !loading) {
          e.currentTarget.style.backgroundColor = style.hoverBg;
          e.currentTarget.style.transform = 'translateY(-1px)';
        }
      }}
      onMouseLeave={(e) => {
        if (!disabled && !loading) {
          e.currentTarget.style.backgroundColor = style.bg;
          e.currentTarget.style.transform = 'translateY(0)';
        }
      }}
      onFocus={(e) => {
        if (!disabled && !loading) {
          e.currentTarget.style.boxShadow = `0 0 0 2px ${style.color}40`;
        }
      }}
      onBlur={(e) => {
        if (!disabled && !loading) {
          e.currentTarget.style.boxShadow = 'none';
        }
      }}
      title={label}
      aria-label={label}
    >
      {loading ? (
        <Loader2 size={14} className="animate-spin" />
      ) : (
        <Icon size={14} />
      )}
      <span>{label}</span>
    </button>
  );
};

// Enhanced user row component
const UserRow = ({ 
  user, 
  isSelected, 
  onSelect, 
  onUserAction, 
  theme, 
  currentThemeName,
  actionLoading,
  onViewDetails
}) => {
  const [showActions, setShowActions] = useState(false);
  const [loading, setLoading] = useState(false);

  const handleAction = async (action) => {
    setLoading(true);
    try {
      await onUserAction(user.id, action);
    } finally {
      setLoading(false);
      setShowActions(false);
    }
  };

  const getBusinessIcon = (businessType) => {
    const icons = {
      'Technology': Globe,
      'Restaurant': Building,
      'Transportation': Building,
      'Retail': Building,
      'Healthcare': Building,
      'Education': Building
    };
    return icons[businessType] || Building;
  };

  const BusinessIcon = getBusinessIcon(user.businessType);

  return (
    <tr 
      style={{
        borderBottom: currentThemeName === 'dark' 
          ? '1px solid rgba(59, 130, 246, 0.1)' 
          : '1px solid rgba(59, 130, 246, 0.05)',
        transition: 'all 0.2s ease',
        backgroundColor: isSelected 
          ? (currentThemeName === 'dark' ? 'rgba(59, 130, 246, 0.1)' : 'rgba(59, 130, 246, 0.05)')
          : 'transparent'
      }}
      onMouseEnter={(e) => {
        e.currentTarget.style.backgroundColor = currentThemeName === 'dark' 
          ? 'rgba(59, 130, 246, 0.05)' 
          : 'rgba(59, 130, 246, 0.02)';
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.backgroundColor = isSelected 
          ? (currentThemeName === 'dark' ? 'rgba(59, 130, 246, 0.1)' : 'rgba(59, 130, 246, 0.05)')
          : 'transparent';
      }}
    >
      {/* Checkbox */}
      <td style={{ padding: '16px 8px', width: 48 }}>
        <input
          type="checkbox"
          checked={isSelected}
          onChange={() => onSelect(user.id)}
          style={{
            width: 20,
            height: 20,
            accentColor: currentThemeName === 'dark' ? '#10b981' : '#10b981',
            cursor: 'pointer',
            borderRadius: '4px',
            border: currentThemeName === 'dark' 
              ? '2px solid rgba(16, 185, 129, 0.3)' 
              : '2px solid rgba(16, 185, 129, 0.2)',
            backgroundColor: currentThemeName === 'dark' 
              ? 'rgba(16, 185, 129, 0.1)' 
              : 'rgba(16, 185, 129, 0.05)',
            transition: 'all 0.2s ease'
          }}
          aria-label={`Select ${user.name}`}
        />
      </td>

      {/* User Info */}
      <td style={{ padding: '16px 12px' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <div style={{
            width: 40,
            height: 40,
            borderRadius: '50%',
            background: currentThemeName === 'dark' 
              ? 'linear-gradient(135deg, #3b82f6, #1d4ed8)' 
              : 'linear-gradient(135deg, #3b82f6, #1d4ed8)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            color: 'white',
            fontWeight: 600,
            fontSize: 14
          }}>
            {(user.first_name?.charAt(0) || user.last_name?.charAt(0))?.toUpperCase() || 'U'}
          </div>
          <div>
            <div style={{ 
              fontWeight: 600, 
              color: currentThemeName === 'dark' ? 'white' : theme.text,
              marginBottom: 2,
              display: 'flex',
              alignItems: 'center',
              gap: 8
            }}>
              {`${user.first_name || ''} ${user.last_name || ''}`.trim() || 'Unknown User'}
              {(user.is_admin || user.isAdmin) && (
                <span style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 4,
                  padding: '2px 8px',
                  borderRadius: 999,
                  fontSize: 11,
                  fontWeight: 700,
                  background: 'rgba(245, 158, 11, 0.15)',
                  color: '#f59e0b',
                  border: '1px solid rgba(245, 158, 11, 0.35)'
                }}>
                  <Crown size={11} />
                  Admin
                </span>
              )}
            </div>
            <div style={{ 
              fontSize: 12, 
              color: currentThemeName === 'dark' ? '#9ca3af' : theme.textTertiary,
              display: 'flex',
              alignItems: 'center',
              gap: 4
            }}>
              <BusinessIcon size={12} />
              {user.business_type || 'Unknown'}
            </div>
          </div>
        </div>
      </td>

      {/* Email */}
      <td style={{ padding: '16px 12px' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          <Mail size={14} style={{ color: currentThemeName === 'dark' ? '#6b7280' : theme.textTertiary }} />
          <span style={{ 
            color: currentThemeName === 'dark' ? '#e5e7eb' : theme.textSecondary,
            fontSize: 14
          }}>
            {user.email}
          </span>
        </div>
      </td>

      {/* Status */}
      <td style={{ padding: '16px 12px' }}>
        <StatusBadge status={user.status} theme={theme} currentThemeName={currentThemeName} />
      </td>

      {/* Signup Date */}
      <td style={{ padding: '16px 12px' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          <Calendar size={14} style={{ color: currentThemeName === 'dark' ? '#6b7280' : theme.textTertiary }} />
          <span style={{ 
            color: currentThemeName === 'dark' ? '#e5e7eb' : theme.textSecondary,
            fontSize: 14
          }}>
            {formatDate(user.created_at)}
          </span>
        </div>
      </td>

      {/* Last Login */}
      <td style={{ padding: '16px 12px' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          <ClockIcon size={14} style={{ color: currentThemeName === 'dark' ? '#6b7280' : theme.textTertiary }} />
          <span style={{ 
            color: currentThemeName === 'dark' ? '#e5e7eb' : theme.textSecondary,
            fontSize: 14
          }}>
            {formatDate(user.last_login)}
          </span>
        </div>
      </td>

      {/* Actions */}
      <td style={{ padding: '16px 12px', position: 'relative' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          {user.status === 'active' ? (
            <>
              <ActionButton
                icon={ShieldX}
                label="Block"
                variant="danger"
                onClick={() => handleAction('block')}
                loading={loading && actionLoading === `block-${user.id}`}
                theme={theme}
                currentThemeName={currentThemeName}
              />
              <ActionButton
                icon={UserMinus}
                label="Stop"
                variant="secondary"
                onClick={() => handleAction('stop')}
                loading={loading && actionLoading === `stop-${user.id}`}
                theme={theme}
                currentThemeName={currentThemeName}
              />
            </>
          ) : user.status === 'blocked' ? (
            <ActionButton
              icon={ShieldCheck}
              label="Activate"
              variant="primary"
              onClick={() => handleAction('activate')}
              loading={loading && actionLoading === `activate-${user.id}`}
              theme={theme}
              currentThemeName={currentThemeName}
            />
          ) : user.status === 'pending' ? (
            <>
              <ActionButton
                icon={Check}
                label="Approve"
                variant="primary"
                onClick={() => handleAction('approve')}
                loading={loading && actionLoading === `approve-${user.id}`}
                theme={theme}
                currentThemeName={currentThemeName}
              />
              <ActionButton
                icon={X}
                label="Block"
                variant="danger"
                onClick={() => handleAction('block')}
                loading={loading && actionLoading === `block-${user.id}`}
                theme={theme}
                currentThemeName={currentThemeName}
              />
            </>
          ) : (
            <ActionButton
              icon={UserCheck}
              label="Activate"
              variant="primary"
              onClick={() => handleAction('activate')}
              loading={loading && actionLoading === `activate-${user.id}`}
              theme={theme}
              currentThemeName={currentThemeName}
            />
          )}

          {/* Admin status buttons */}
          {user.is_admin || user.isAdmin ? (
            <ActionButton
              icon={UserCog}
              label="Remove Admin"
              variant="secondary"
              onClick={() => handleAction('removeAdmin')}
              loading={loading && actionLoading === `removeAdmin-${user.id}`}
              theme={theme}
              currentThemeName={currentThemeName}
            />
          ) : (
            <ActionButton
              icon={Crown}
              label="Set As Admin"
              variant="primary"
              onClick={() => handleAction('setAdmin')}
              loading={loading && actionLoading === `setAdmin-${user.id}`}
              theme={theme}
              currentThemeName={currentThemeName}
            />
          )}

          {/* More actions dropdown */}
          <div style={{ position: 'relative' }}>
            <button
              onClick={() => setShowActions(!showActions)}
              style={{
                padding: '6px',
                borderRadius: 6,
                border: 'none',
                background: 'transparent',
                color: currentThemeName === 'dark' ? '#9ca3af' : theme.textTertiary,
                cursor: 'pointer',
                transition: 'all 0.2s ease'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.backgroundColor = currentThemeName === 'dark' 
                  ? 'rgba(59, 130, 246, 0.1)' 
                  : 'rgba(59, 130, 246, 0.1)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.backgroundColor = 'transparent';
              }}
              aria-label="More actions"
            >
              <MoreHorizontal size={16} />
            </button>

            {showActions && (
              <div style={{
                position: 'absolute',
                top: '100%',
                right: 0,
                background: currentThemeName === 'dark' 
                  ? 'rgba(30, 41, 59, 0.95)' 
                  : 'rgba(255, 255, 255, 0.95)',
                border: currentThemeName === 'dark'
                  ? '1px solid rgba(59, 130, 246, 0.2)'
                  : '1px solid rgba(59, 130, 246, 0.1)',
                borderRadius: 8,
                boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
                zIndex: 1000,
                minWidth: 160,
                backdropFilter: 'blur(10px)'
              }}>
                <button
                  onClick={() => {
                    setShowActions(false);
                    onViewDetails(user);
                  }}
                  style={{
                    width: '100%',
                    padding: '8px 12px',
                    border: 'none',
                    background: 'transparent',
                    color: currentThemeName === 'dark' ? '#e5e7eb' : theme.text,
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    gap: 8,
                    fontSize: 14,
                    transition: 'background-color 0.2s ease'
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.backgroundColor = currentThemeName === 'dark' 
                      ? 'rgba(59, 130, 246, 0.1)' 
                      : 'rgba(59, 130, 246, 0.05)';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.backgroundColor = 'transparent';
                  }}
                >
                  <Eye size={14} />
                  View Details
                </button>
                <button
                  onClick={() => {
                    setShowActions(false);
                    handleAction('delete');
                  }}
                  style={{
                    width: '100%',
                    padding: '8px 12px',
                    border: 'none',
                    background: 'transparent',
                    color: '#ef4444',
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    gap: 8,
                    fontSize: 14,
                    transition: 'background-color 0.2s ease'
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.backgroundColor = 'rgba(239, 68, 68, 0.1)';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.backgroundColor = 'transparent';
                  }}
                >
                  <Trash2 size={14} />
                  Delete User
                </button>
              </div>
            )}
          </div>
        </div>
      </td>
    </tr>
  );
};

function UserTable({ 
  users, 
  filteredUsers, 
  selectedUsers, 
  setSelectedUsers, 
  onRefresh, 
  onUserAction, 
  currentPage, 
  setCurrentPage, 
  totalPages, 
  itemsPerPage,
  theme,
  currentThemeName,
  loading = false,
  refreshing = false,
  totalUsers = 0,
  loadError = null
}) {
  const [actionLoading, setActionLoading] = useState(null);
  const [selectedUser, setSelectedUser] = useState(null);
  const [showUserDetails, setShowUserDetails] = useState(false);

  const handleUserAction = async (userId, action) => {
    setActionLoading(`${action}-${userId}`);
    try {
      await onUserAction(userId, action);
    } finally {
      setActionLoading(null);
    }
  };

  const handleSelectAll = () => {
    if (selectedUsers.length === filteredUsers.length) {
      setSelectedUsers([]);
    } else {
      setSelectedUsers(filteredUsers.map(user => user.id));
    }
  };

  const isAllSelected = filteredUsers.length > 0 && selectedUsers.length === filteredUsers.length;
  const isIndeterminate = selectedUsers.length > 0 && selectedUsers.length < filteredUsers.length;

  return (
    <div style={{
      background: currentThemeName === 'dark'
        ? 'linear-gradient(135deg, rgba(30, 41, 59, 0.9) 0%, rgba(15, 23, 42, 0.95) 100%)'
        : currentThemeName === 'beige'
        ? 'linear-gradient(135deg, rgba(245, 245, 220, 0.95) 0%, rgba(250, 235, 215, 0.98) 100%)'
        : 'linear-gradient(135deg, rgba(255, 255, 255, 0.98) 0%, rgba(248, 250, 252, 0.99) 100%)',
      border: currentThemeName === 'dark'
        ? '1px solid rgba(59, 130, 246, 0.2)'
        : currentThemeName === 'beige'
        ? '1px solid rgba(139, 69, 19, 0.15)'
        : '1px solid rgba(59, 130, 246, 0.1)',
      borderRadius: 20,
      overflow: 'hidden',
      boxShadow: currentThemeName === 'dark'
        ? '0 8px 32px rgba(0, 0, 0, 0.3), 0 0 0 1px rgba(59, 130, 246, 0.1)'
        : currentThemeName === 'beige'
        ? '0 8px 32px rgba(139, 69, 19, 0.1), 0 0 0 1px rgba(139, 69, 19, 0.05)'
        : '0 8px 32px rgba(59, 130, 246, 0.08), 0 0 0 1px rgba(59, 130, 246, 0.05)',
      backdropFilter: 'blur(10px)',
      position: 'relative'
    }}>
      {/* Table Header */}
      <div style={{
        padding: '20px 24px',
        borderBottom: currentThemeName === 'dark' 
          ? '1px solid rgba(59, 130, 246, 0.1)' 
          : '1px solid rgba(59, 130, 246, 0.05)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between'
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <div style={{
            width: 40,
            height: 40,
            borderRadius: 12,
            background: currentThemeName === 'dark' 
              ? 'linear-gradient(135deg, #3b82f6, #1d4ed8)' 
              : 'linear-gradient(135deg, #3b82f6, #1d4ed8)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            color: 'white'
          }}>
            <Users size={20} />
          </div>
          <div>
            <div style={{ 
              fontSize: 18, 
              fontWeight: 600, 
              color: currentThemeName === 'dark' ? 'white' : theme.text 
            }}>
              Users ({totalUsers > 0 ? totalUsers : filteredUsers.length})
            </div>
            <div style={{ 
              fontSize: 14, 
              color: currentThemeName === 'dark' ? '#9ca3af' : theme.textTertiary 
            }}>
              Manage user accounts and permissions
            </div>
          </div>
        </div>

        <button
          onClick={onRefresh}
          disabled={refreshing}
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 8,
            padding: '8px 16px',
            borderRadius: 8,
            border: 'none',
            background: currentThemeName === 'dark' 
              ? 'rgba(59, 130, 246, 0.1)' 
              : 'rgba(59, 130, 246, 0.1)',
            color: currentThemeName === 'dark' ? '#3b82f6' : '#3b82f6',
            cursor: refreshing ? 'not-allowed' : 'pointer',
            transition: 'all 0.2s ease',
            opacity: refreshing ? 0.6 : 1
          }}
          onMouseEnter={(e) => {
            if (!refreshing) {
              e.currentTarget.style.background = currentThemeName === 'dark' 
                ? 'rgba(59, 130, 246, 0.2)' 
                : 'rgba(59, 130, 246, 0.2)';
            }
          }}
          onMouseLeave={(e) => {
            if (!refreshing) {
              e.currentTarget.style.background = currentThemeName === 'dark' 
                ? 'rgba(59, 130, 246, 0.1)' 
                : 'rgba(59, 130, 246, 0.1)';
            }
          }}
          aria-label="Refresh user list"
        >
          <RefreshCw size={16} className={refreshing ? 'animate-spin' : ''} />
          <span>Refresh</span>
        </button>
      </div>

      {/* Loading State */}
      {loading && (
        <div style={{
          padding: '60px 24px',
          textAlign: 'center',
          background: currentThemeName === 'dark' ? 'rgba(15, 23, 42, 0.6)' : 'rgba(248, 250, 252, 0.9)',
          borderRadius: 12,
          color: currentThemeName === 'dark' ? '#94a3b8' : theme.textTertiary
        }}>
          <Loader2 size={32} className="animate-spin" style={{ marginBottom: 16 }} />
          <div style={{ fontSize: 16, fontWeight: 500 }}>Loading users...</div>
        </div>
      )}

      {/* Empty State */}
      {!loading && filteredUsers.length === 0 && (
        <div style={{
          padding: '60px 24px',
          textAlign: 'center',
          color: currentThemeName === 'dark' ? '#9ca3af' : theme.textTertiary
        }}>
          <Users size={48} style={{ marginBottom: 16, opacity: 0.5 }} />
          <div style={{ fontSize: 18, fontWeight: 600, marginBottom: 8 }}>
            No users found
          </div>
          <div style={{ fontSize: 14 }}>
            {loadError
              ? 'Could not load users. Check that the backend is running and REACT_APP_API_URL is correct, then rebuild the frontend.'
              : users.length === 0
                ? 'Database has no users yet. From project root run: npm run seed:admin'
                : 'Try adjusting your search or filter criteria'}
          </div>
        </div>
      )}

      {/* Table Content */}
      {!loading && filteredUsers.length > 0 && (
        <>
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr style={{
                  background: currentThemeName === 'dark' 
                    ? 'rgba(59, 130, 246, 0.05)' 
                    : 'rgba(59, 130, 246, 0.02)',
                  borderBottom: currentThemeName === 'dark' 
                    ? '1px solid rgba(59, 130, 246, 0.1)' 
                    : '1px solid rgba(59, 130, 246, 0.05)'
                }}>
                  <th style={{ 
                    padding: '16px 8px', 
                    textAlign: 'left', 
                    width: 48,
                    color: currentThemeName === 'dark' ? '#9ca3af' : theme.textTertiary,
                    fontSize: 12,
                    fontWeight: 600,
                    textTransform: 'uppercase',
                    letterSpacing: 0.5
                  }}>
                    <input
                      type="checkbox"
                      checked={isAllSelected}
                      ref={(input) => {
                        if (input) input.indeterminate = isIndeterminate;
                      }}
                      onChange={handleSelectAll}
                      style={{
                        width: 20,
                        height: 20,
                        accentColor: currentThemeName === 'dark' ? '#10b981' : '#10b981',
                        cursor: 'pointer',
                        borderRadius: '4px',
                        border: currentThemeName === 'dark' 
                          ? '2px solid rgba(16, 185, 129, 0.3)' 
                          : '2px solid rgba(16, 185, 129, 0.2)',
                        backgroundColor: currentThemeName === 'dark' 
                          ? 'rgba(16, 185, 129, 0.1)' 
                          : 'rgba(16, 185, 129, 0.05)',
                        transition: 'all 0.2s ease'
                      }}
                      aria-label="Select all users"
                    />
                  </th>
                  <th style={{ 
                    padding: '16px 12px', 
                    textAlign: 'left',
                    color: currentThemeName === 'dark' ? '#9ca3af' : theme.textTertiary,
                    fontSize: 12,
                    fontWeight: 600,
                    textTransform: 'uppercase',
                    letterSpacing: 0.5
                  }}>
                    User
                  </th>
                  <th style={{ 
                    padding: '16px 12px', 
                    textAlign: 'left',
                    color: currentThemeName === 'dark' ? '#9ca3af' : theme.textTertiary,
                    fontSize: 12,
                    fontWeight: 600,
                    textTransform: 'uppercase',
                    letterSpacing: 0.5
                  }}>
                    Email
                  </th>
                  <th style={{ 
                    padding: '16px 12px', 
                    textAlign: 'left',
                    color: currentThemeName === 'dark' ? '#9ca3af' : theme.textTertiary,
                    fontSize: 12,
                    fontWeight: 600,
                    textTransform: 'uppercase',
                    letterSpacing: 0.5
                  }}>
                    Status
                  </th>
                  <th style={{ 
                    padding: '16px 12px', 
                    textAlign: 'left',
                    color: currentThemeName === 'dark' ? '#9ca3af' : theme.textTertiary,
                    fontSize: 12,
                    fontWeight: 600,
                    textTransform: 'uppercase',
                    letterSpacing: 0.5
                  }}>
                    Signup Date
                  </th>
                  <th style={{ 
                    padding: '16px 12px', 
                    textAlign: 'left',
                    color: currentThemeName === 'dark' ? '#9ca3af' : theme.textTertiary,
                    fontSize: 12,
                    fontWeight: 600,
                    textTransform: 'uppercase',
                    letterSpacing: 0.5
                  }}>
                    Last Login
                  </th>
                  <th style={{ 
                    padding: '16px 12px', 
                    textAlign: 'left',
                    color: currentThemeName === 'dark' ? '#9ca3af' : theme.textTertiary,
                    fontSize: 12,
                    fontWeight: 600,
                    textTransform: 'uppercase',
                    letterSpacing: 0.5
                  }}>
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody>
                {filteredUsers.map((user) => (
                  <UserRow
                    key={user.id}
                    user={user}
                    isSelected={selectedUsers.includes(user.id)}
                    onSelect={(userId) => {
                      if (selectedUsers.includes(userId)) {
                        setSelectedUsers(selectedUsers.filter(id => id !== userId));
                      } else {
                        setSelectedUsers([...selectedUsers, userId]);
                      }
                    }}
                    onUserAction={handleUserAction}
                    theme={theme}
                    currentThemeName={currentThemeName}
                    actionLoading={actionLoading}
                    onViewDetails={(user) => {
                      setSelectedUser(user);
                      setShowUserDetails(true);
                    }}
                  />
                ))}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          {totalPages > 1 && (
            <div style={{
              padding: '20px 24px',
              borderTop: currentThemeName === 'dark' 
                ? '1px solid rgba(59, 130, 246, 0.1)' 
                : '1px solid rgba(59, 130, 246, 0.05)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between'
            }}>
              <div style={{ 
                fontSize: 14, 
                color: currentThemeName === 'dark' ? '#9ca3af' : theme.textTertiary 
              }}>
                Showing {((currentPage - 1) * itemsPerPage) + 1} to {Math.min(currentPage * itemsPerPage, totalUsers || filteredUsers.length)} of {totalUsers || filteredUsers.length} users
              </div>

              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <button
                  onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
                  disabled={currentPage === 1}
                  style={{
                    padding: '8px 12px',
                    borderRadius: 6,
                    border: 'none',
                    background: currentPage === 1 
                      ? (currentThemeName === 'dark' ? 'rgba(107, 114, 128, 0.1)' : 'rgba(107, 114, 128, 0.1)')
                      : (currentThemeName === 'dark' ? 'rgba(59, 130, 246, 0.1)' : 'rgba(59, 130, 246, 0.1)'),
                    color: currentPage === 1 
                      ? (currentThemeName === 'dark' ? '#6b7280' : '#6b7280')
                      : (currentThemeName === 'dark' ? '#3b82f6' : '#3b82f6'),
                    cursor: currentPage === 1 ? 'not-allowed' : 'pointer',
                    transition: 'all 0.2s ease',
                    opacity: currentPage === 1 ? 0.5 : 1
                  }}
                  aria-label="Previous page"
                >
                  <ChevronLeft size={16} />
                </button>

                <div style={{ 
                  fontSize: 14, 
                  fontWeight: 600,
                  color: currentThemeName === 'dark' ? 'white' : theme.text,
                  padding: '0 12px'
                }}>
                  {currentPage} of {totalPages}
                </div>

                <button
                  onClick={() => setCurrentPage(Math.min(totalPages, currentPage + 1))}
                  disabled={currentPage === totalPages}
                  style={{
                    padding: '8px 12px',
                    borderRadius: 6,
                    border: 'none',
                    background: currentPage === totalPages 
                      ? (currentThemeName === 'dark' ? 'rgba(107, 114, 128, 0.1)' : 'rgba(107, 114, 128, 0.1)')
                      : (currentThemeName === 'dark' ? 'rgba(59, 130, 246, 0.1)' : 'rgba(59, 130, 246, 0.1)'),
                    color: currentPage === totalPages 
                      ? (currentThemeName === 'dark' ? '#6b7280' : '#6b7280')
                      : (currentThemeName === 'dark' ? '#3b82f6' : '#3b82f6'),
                    cursor: currentPage === totalPages ? 'not-allowed' : 'pointer',
                    transition: 'all 0.2s ease',
                    opacity: currentPage === totalPages ? 0.5 : 1
                  }}
                  aria-label="Next page"
                >
                  <ChevronRight size={16} />
                </button>
              </div>
            </div>
          )}
        </>
      )}

      {/* User Details Popup */}
      <UserDetailsPopup
        user={selectedUser}
        isOpen={showUserDetails}
        onClose={() => {
          setShowUserDetails(false);
          setSelectedUser(null);
        }}
        theme={theme}
        currentThemeName={currentThemeName}
      />
    </div>
  );
}

export default UserTable; 